import { useAppContext } from 'src/components/app-context';
import { IconChevronRight } from '@prisjakt/ui/icon/chevron-right';
import React from 'react';
import { Link } from 'react-router-dom';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import type { Award } from 'src/types';
import { usePriceRowContext } from '../../../price-row-context';
import { OfferPaymentProviders } from '../payment-provider';
import { DeliveryOptions } from './delivery-options';
import { RecommendedRetailer } from './recommended-retailer';
import { StoreInfoList } from './store-info-list';
import { StoreReviews } from './store-reviews';
import { MembershipInfo } from './membership-info';
import {
  StyledExpandedInfo,
  StyledGeneralInfo,
  StyledLocationAndCurrencyInfo,
  StyledSotyBadge,
  StyledStoreLink,
} from './styles';

interface IStoreInfo {
  sotyBadge?: Award | null;
}

export const StoreInfo: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<IStoreInfo>>
> = props => {
  const { sotyBadge } = props;
  const { offer, product } = usePriceRowContext();
  const { authorizedDealer, priceId, store, storeInfo } = offer;
  const { t } = useAppContext();

  if (!store) {
    return null;
  }

  return (
    <StyledExpandedInfo>
      <div>
        <StyledStoreLink
          container={Link}
          containerProps={{
            to: store.pathName,
            title: store.name,
            'data-test': 'StoreInfoLink',
            'aria-label': t('product', 'store-info.more-about-store', {
              store_name: store.name,
            }),
            tabIndex: 0,
            onClick: () =>
              trackInternalNavigationClick({
                scope: InternalNavigationScope.STORE_GO_TO,
                nextPage: store.pathName,
              }),
          }}
        >
          {store.name}
          <IconChevronRight />
        </StyledStoreLink>
        {store.userReviewSummary.countTotal &&
        store.userReviewSummary.countTotal > 0 ? (
          <StoreReviews
            reviewCount={store.userReviewSummary.countTotal}
            storeLink={store.pathName}
          />
        ) : null}
        <StoreInfoList storeInfo={storeInfo} />
      </div>

      {sotyBadge?.logo ? (
        <StyledSotyBadge
          imgUrl={sotyBadge.logo}
          name={sotyBadge.name}
          year={sotyBadge.year?.toString()}
        />
      ) : null}

      <StyledGeneralInfo>
        <StyledLocationAndCurrencyInfo
          countryCode={store.countryCode}
          countryName={store.countryName}
          currency={store.currency}
          marketplace={Boolean(store.marketplace)}
          id={priceId || store.id.toString()}
          storeName={store.name}
          minify
          textSize="small"
        />

        {authorizedDealer && product.brand && offer.store && (
          <RecommendedRetailer
            id={product.brand.id}
            pathName={product.brand.pathName}
            logo={product.brand.logo}
            brandName={product.brand.name}
            storeName={offer.store.name}
          />
        )}

        <MembershipInfo />
        <OfferPaymentProviders store={store} showAll />
        <DeliveryOptions offer={offer} />
      </StyledGeneralInfo>
    </StyledExpandedInfo>
  );
};
